/**
 * CircleComponent - Renders a circular boundary with transparent fill
 * Only the stroke (border) is visible, inside is transparent
 */

import React, { useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";

interface CircleComponentProps {
    center: { lat: number; lng: number };
    radius: number; // radius in kilometers
    strokeColor?: string;
    strokeOpacity?: number;
    strokeWeight?: number;
}

export const CircleComponent: React.FC<CircleComponentProps> = ({
    center,
    radius,
    strokeColor = "green",
    strokeOpacity = 0.8,
    strokeWeight = 2
}) => {
    const map = useMap();
    const circleRef = useRef<google.maps.Circle | null>(null);

    useEffect(() => {
        if (!map) return;

        // Convert radius from kilometers to meters (Google Maps Circle uses meters)
        const radiusMeters = radius * 1000;

        if (circleRef.current) {
            // Update existing circle
            circleRef.current.setOptions({
                center: center,
                radius: radiusMeters,
                strokeColor,
                strokeOpacity,
                strokeWeight,
                fillColor: strokeColor,
                fillOpacity: 0.0, // Transparent inside
                zIndex: 10, // Above polygons
                clickable: false // Don't block mouse events
            });
        } else {
            // Create new circle
            const circle = new google.maps.Circle({
                center: center,
                radius: radiusMeters,
                strokeColor,
                strokeOpacity,
                strokeWeight,
                fillColor: strokeColor,
                fillOpacity: 0.0, // Transparent inside
                zIndex: 10, // Above polygons
                clickable: false // Don't block mouse events
            });
            circle.setMap(map);
            circleRef.current = circle;
        }

        // Cleanup function
        return () => {
            if (circleRef.current) {
                circleRef.current.setMap(null);
                circleRef.current = null;
            }
        };
    }, [map, center, radius, strokeColor, strokeOpacity, strokeWeight]);

    return null;
};
